// src/utils/safeRender.js
/**
 * Safely render any value as a React child
 * Prevents React error #130 (Objects are not valid as a React child)
 * 
 * @param {any} value - The value to render
 * @param {string} fallback - Fallback string if value cannot be rendered (default: "")
 * @returns {string|number|React.ReactNode} - Safe value to render
 */
export const safeRender = (value, fallback = "") => {
  // Handle null/undefined
  if (value === null || value === undefined) {
    return fallback;
  }

  // Handle primitives (string, number, boolean)
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
    return value;
  }

  // Handle arrays - join them or render as list
  if (Array.isArray(value)) {
    if (value.length === 0) {
      return fallback;
    }
    // If all items are primitives, join them
    if (value.every(item => typeof item === "string" || typeof item === "number")) {
      return value.join(", ");
    }
    // Otherwise, try to stringify each item
    return value.map(item => safeRender(item, "")).filter(Boolean).join(", ");
  }

  // Handle Error objects
  if (value instanceof Error) {
    return value.message || fallback;
  }

  // Handle Date objects
  if (value instanceof Date) {
    return value.toISOString();
  }

  // Handle objects - try to extract meaningful string representation
  if (typeof value === "object") {
    // Try common object properties
    if (value.message && typeof value.message === "string") {
      return value.message;
    }
    if (value.error && typeof value.error === "string") {
      return value.error;
    }
    if (value.text && typeof value.text === "string") {
      return value.text;
    }
    if (value.content && typeof value.content === "string") {
      return value.content;
    }
    if (value.name && typeof value.name === "string") {
      return value.name;
    }
    if (value.toString && typeof value.toString === "function") {
      try {
        const str = value.toString();
        if (str !== "[object Object]") {
          return str;
        }
      } catch (e) {
        // Ignore toString errors
      }
    }
    // Last resort: JSON stringify (but avoid circular references)
    try {
      return JSON.stringify(value);
    } catch (e) {
      return fallback;
    }
  }

  // Fallback for any other type
  return fallback;
};

/**
 * React component wrapper that safely renders children
 * Use this to wrap any content that might be an object
 */
export const SafeRender = ({ children, fallback = "" }) => {
  return safeRender(children, fallback);
};



